<template>
  <div class="consumo-tab">
    <!-- Reset header -->
    <div class="consumo-reset">
      <div class="consumo-reset-left">
        <div class="consumo-reset-icon">
          <i class="el-icon-refresh-right"></i>
        </div>
        <div class="consumo-reset-text">
          <div class="consumo-reset-title">
            Tu plan se renueva en
            <strong
              >{{ daysUntilReset }}
              {{ daysUntilReset === 1 ? "día" : "días" }}</strong
            >
          </div>
          <div class="consumo-reset-subtitle">
            Los consumos se reinician el {{ resetDateFormatted }}
          </div>
        </div>
      </div>
      <div v-if="isAnnualBilling" class="consumo-badge-annual">
        <i class="el-icon-medal"></i> Plan anual
      </div>
    </div>

    <p v-if="isAnnualBilling" class="consumo-annual-note">
      Aunque tu plan está pagado anualmente, los límites de consumo se aplican
      mes a mes y se reinician cada inicio de mes.
    </p>

    <!-- Loading -->
    <div v-if="!userPlanLoaded" class="consumo-state">
      <i class="el-icon-loading"></i> Cargando consumo del plan...
    </div>

    <!-- No plan -->
    <div v-else-if="!userPlan" class="consumo-state consumo-state--empty">
      No tienes un plan activo.
      <router-link :to="{ name: 'plans' }">Ver planes disponibles</router-link>
    </div>

    <!-- Usage bars -->
    <div v-else class="consumo-bars">
      <div
        v-for="bar in bars"
        :key="bar.key"
        class="consumo-bar"
        :class="{
          'is-blocked': bar.blocked,
          'is-not-included': bar.notIncluded
        }"
      >
        <div class="consumo-bar-head">
          <div class="consumo-bar-title">
            <i :class="['consumo-bar-icon', bar.icon]"></i>
            <div>
              <div class="consumo-bar-label">{{ bar.title }}</div>
              <div class="consumo-bar-desc">{{ bar.description }}</div>
            </div>
          </div>
          <div class="consumo-bar-count">
            <template v-if="bar.unlimited">
              <span class="consumo-pill consumo-pill--ok">
                <i class="el-icon-infinite"></i> Ilimitado
              </span>
            </template>
            <template v-else-if="bar.notIncluded">
              <span class="consumo-pill consumo-pill--muted">
                No incluido en tu plan
              </span>
            </template>
            <template v-else>
              <strong>{{ formatNumber(bar.used) }}</strong>
              <span class="consumo-bar-sep">/</span>
              <span>{{ formatNumber(bar.limit) }}</span>
              <span
                class="consumo-pill"
                :class="severityPillClass(bar.percentage)"
              >
                {{ bar.percentage }}%
              </span>
            </template>
          </div>
        </div>

        <div
          v-if="!bar.unlimited && !bar.notIncluded"
          class="consumo-bar-track"
        >
          <div
            class="consumo-bar-fill"
            :class="severityFillClass(bar.percentage)"
            :style="{ width: Math.min(100, bar.percentage) + '%' }"
          ></div>
        </div>

        <div
          v-if="!bar.unlimited && bar.blocked"
          class="consumo-bar-blocked-msg"
        >
          <i class="el-icon-warning"></i>
          Límite alcanzado. No podrás consumir más este mes hasta el reinicio o
          hasta que mejores tu plan.
        </div>

        <div v-if="bar.notIncluded" class="consumo-bar-muted-msg">
          <i class="el-icon-lock"></i>
          Esta función no está incluida en tu plan.
          <router-link :to="{ name: 'plans' }">Mejora tu plan</router-link>
          para activarla.
        </div>
      </div>
    </div>

    <!-- CTA upgrade -->
    <div
      v-if="userPlan && shouldShowCta"
      class="consumo-cta"
      :class="ctaSeverityClass"
    >
      <div class="consumo-cta-icon">
        <i :class="ctaIcon"></i>
      </div>
      <div class="consumo-cta-body">
        <div class="consumo-cta-title">{{ ctaTitle }}</div>
        <div class="consumo-cta-text">{{ ctaText }}</div>
      </div>
      <router-link :to="{ name: 'plans' }" class="consumo-cta-btn">
        Mejorar plan <i class="el-icon-arrow-right"></i>
      </router-link>
    </div>

    <!-- Plan footer -->
    <div v-if="userPlan && userPlan.plan" class="consumo-footer">
      <div class="consumo-footer-row">
        <span class="consumo-footer-label">Plan actual</span>
        <span class="consumo-footer-value">{{ planLabel }}</span>
      </div>
      <router-link :to="{ name: 'plans' }" class="consumo-footer-link">
        Ver todos los planes
      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConsumoTab",
  computed: {
    userPlan() {
      return this.$store.getters.userPlan;
    },
    userPlanLoaded() {
      return this.$store.getters.userPlanLoaded;
    },
    isAnnualBilling() {
      const period =
        this.userPlan?.subscription?.billing_period ||
        this.userPlan?.billing_period ||
        this.userPlan?.plan?.billing_period;
      return period === "annual" || period === "yearly";
    },
    planLabel() {
      const plan = this.userPlan?.plan;
      if (!plan) return "—";
      return plan.display_name || plan.label || plan.name || plan.slug || "—";
    },
    signing() {
      return (
        this.$store.getters.signingUsage || {
          used: 0,
          limit: null,
          percentage: 0
        }
      );
    },
    emailCampaigns() {
      // API expone esta llave; getter del store usa "bulk_sending" como alias.
      return (
        this.userPlan?.usage?.email_campaigns ||
        this.userPlan?.usage?.bulk_sending || {
          used: 0,
          limit: 0,
          percentage: 0
        }
      );
    },
    ai() {
      return (
        this.$store.getters.aiUsage || { used: 0, limit: null, percentage: 0 }
      );
    },
    bars() {
      const make = cfg => {
        const usage = cfg.usage;
        const limit = usage ? usage.limit : undefined;
        const used = (usage && usage.used) || 0;
        const unlimited = limit === null || limit === undefined;
        const notIncluded = !unlimited && limit === 0;
        return {
          key: cfg.key,
          title: cfg.title,
          description: cfg.description,
          icon: cfg.icon,
          used,
          limit,
          percentage: this.computePercent(usage),
          unlimited,
          notIncluded,
          blocked: !unlimited && !notIncluded && used >= limit
        };
      };
      return [
        make({
          key: "signing",
          title: "Documentos firmados al SRI",
          description:
            "Facturas, notas de crédito/débito, liquidaciones, guías de remisión y retenciones.",
          icon: "el-icon-document-checked",
          usage: this.signing
        }),
        make({
          key: "email_campaigns",
          title: "Correos masivos",
          description:
            "Envíos de campañas masivas a clientes (newsletters y marketing).",
          icon: "el-icon-message",
          usage: this.emailCampaigns
        }),
        make({
          key: "ai",
          title: "Créditos IA",
          description:
            "Consumo de tu asistente Mydessk IA y funciones inteligentes.",
          icon: "el-icon-magic-stick",
          usage: this.ai
        })
      ];
    },
    maxPercentage() {
      // Sólo cuenta para el CTA si la métrica está incluida en el plan y tiene límite.
      const eligible = this.bars.filter(b => !b.unlimited && !b.notIncluded);
      if (!eligible.length) return 0;
      return Math.max(...eligible.map(b => b.percentage || 0));
    },
    shouldShowCta() {
      return this.maxPercentage >= 80;
    },
    ctaSeverityClass() {
      if (this.maxPercentage >= 100) return "consumo-cta--danger";
      if (this.maxPercentage >= 95) return "consumo-cta--danger";
      return "consumo-cta--warn";
    },
    ctaIcon() {
      if (this.maxPercentage >= 95) return "el-icon-warning";
      return "el-icon-top-right";
    },
    ctaTitle() {
      if (this.maxPercentage >= 100)
        return "Has alcanzado el límite de tu plan";
      if (this.maxPercentage >= 95) return "Estás a punto de agotar tu plan";
      return "Tu consumo está por encima del 80%";
    },
    ctaText() {
      if (this.maxPercentage >= 100) {
        return "Mejora tu plan para seguir creando documentos, enviando correos masivos o usando la IA sin esperar al reinicio mensual.";
      }
      return "Cuando llegues al 100% no podrás seguir consumiendo hasta el siguiente mes. Mejora tu plan para tener más capacidad ahora mismo.";
    },
    resetDate() {
      const ai = this.userPlan?.usage?.ai_credits;
      if (ai && ai.resets_at) {
        // Parsea como fecha local (evita corrimiento UTC con strings "YYYY-MM-DD")
        if (
          typeof ai.resets_at === "string" &&
          /^\d{4}-\d{2}-\d{2}/.test(ai.resets_at)
        ) {
          const [y, m, d] = ai.resets_at
            .slice(0, 10)
            .split("-")
            .map(Number);
          return new Date(y, m - 1, d);
        }
        const d = new Date(ai.resets_at);
        if (!isNaN(d.getTime())) return d;
      }
      // Fallback: primer día del siguiente mes a las 00:00 local
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 1);
    },
    daysUntilReset() {
      const now = new Date();
      const ms = this.resetDate.getTime() - now.getTime();
      const days = Math.ceil(ms / 86400000);
      return Math.max(0, days);
    },
    resetDateFormatted() {
      const months = [
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre"
      ];
      const d = this.resetDate;
      return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
    }
  },
  methods: {
    computePercent(usage) {
      if (!usage || usage.limit === null || usage.limit === undefined) return 0;
      if (typeof usage.percentage === "number" && !isNaN(usage.percentage)) {
        return Math.max(0, Math.min(100, Math.round(usage.percentage)));
      }
      if (!usage.limit) return 0;
      return Math.max(
        0,
        Math.min(100, Math.round(((usage.used || 0) / usage.limit) * 100))
      );
    },
    formatNumber(n) {
      if (n === null || n === undefined) return "0";
      try {
        return Number(n).toLocaleString("es-EC");
      } catch (e) {
        return String(n);
      }
    },
    severityFillClass(p) {
      if (p >= 100) return "is-danger";
      if (p >= 95) return "is-danger";
      if (p >= 80) return "is-warn";
      return "";
    },
    severityPillClass(p) {
      if (p >= 95) return "consumo-pill--danger";
      if (p >= 80) return "consumo-pill--warn";
      return "consumo-pill--ok";
    }
  },
  mounted() {
    if (!this.userPlanLoaded) {
      this.$store.dispatch("fetchUserPlan");
    }
  }
};
</script>

<style scoped lang="scss">
$primary: #31355c;
$accent: #3ecfb4;
$border: #e4eaf6;
$muted: #909399;
$bg: #f7f8f9;
$warn: #e6a23c;
$danger: #f56c6c;
$success: #1d9e75;

.consumo-tab {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Reset header */
.consumo-reset {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #f0fdf9 0%, #f7f8ff 100%);
  border: 1px solid #d6f4ea;
  border-radius: 14px;
}
.consumo-reset-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.consumo-reset-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid $border;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $accent;
  font-size: 22px;
  flex-shrink: 0;
}
.consumo-reset-title {
  font-size: 15px;
  color: $primary;
  font-weight: 500;
  strong {
    font-weight: 700;
    color: $primary;
  }
}
.consumo-reset-subtitle {
  font-size: 12px;
  color: $muted;
  margin-top: 2px;
}
.consumo-badge-annual {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: $primary;
  i {
    color: $accent;
  }
}
.consumo-annual-note {
  margin: -6px 4px 0;
  font-size: 12px;
  color: $muted;
  line-height: 1.45;
}

/* States */
.consumo-state {
  text-align: center;
  padding: 40px 16px;
  color: $muted;
  font-size: 14px;
  background: $bg;
  border-radius: 12px;
  i {
    margin-right: 6px;
  }
  &--empty a {
    color: $accent;
    font-weight: 600;
    margin-left: 6px;
  }
}

/* Bars */
.consumo-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.consumo-bar {
  border: 1px solid $border;
  border-radius: 12px;
  padding: 16px 18px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: darken($border, 6%);
  }
  &.is-blocked {
    border-color: lighten($danger, 18%);
    background: #fff8f8;
  }
  &.is-not-included {
    background: #fafbfc;
    border-style: dashed;
    .consumo-bar-icon {
      color: $muted;
    }
    .consumo-bar-label,
    .consumo-bar-desc {
      color: $muted;
    }
  }
}
.consumo-bar-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.consumo-bar-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.consumo-bar-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: $bg;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: $primary;
  font-size: 16px;
  flex-shrink: 0;
}
.consumo-bar-label {
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  line-height: 1.2;
}
.consumo-bar-desc {
  font-size: 12px;
  color: $muted;
  margin-top: 3px;
  line-height: 1.4;
}
.consumo-bar-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: $primary;
  white-space: nowrap;
  flex-shrink: 0;
  strong {
    font-weight: 700;
  }
  .consumo-bar-sep {
    color: $muted;
  }
}
.consumo-bar-track {
  height: 8px;
  background: $border;
  border-radius: 999px;
  overflow: hidden;
}
.consumo-bar-fill {
  height: 100%;
  background: $accent;
  border-radius: 999px;
  transition: width 0.4s ease, background 0.2s;
  &.is-warn {
    background: $warn;
  }
  &.is-danger {
    background: $danger;
  }
}
.consumo-bar-blocked-msg {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff0f0;
  border: 1px solid lighten($danger, 25%);
  border-radius: 8px;
  font-size: 12px;
  color: darken($danger, 10%);
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    flex-shrink: 0;
  }
}

/* Pills */
.consumo-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 4px;
  &--ok {
    background: #e1f5ee;
    color: $success;
  }
  &--warn {
    background: #fdf6ec;
    color: $warn;
  }
  &--danger {
    background: #fef0f0;
    color: $danger;
  }
  &--muted {
    background: $bg;
    color: $muted;
    border: 1px solid $border;
  }
}

.consumo-bar-muted-msg {
  margin-top: 10px;
  padding: 8px 12px;
  background: $bg;
  border: 1px dashed $border;
  border-radius: 8px;
  font-size: 12px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    flex-shrink: 0;
  }
  a {
    color: $accent;
    font-weight: 600;
    margin: 0 4px;
  }
}

/* CTA */
.consumo-cta {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid;

  &--warn {
    background: #fdf6ec;
    border-color: lighten($warn, 25%);
    .consumo-cta-icon {
      background: $warn;
    }
    .consumo-cta-btn {
      background: $warn;
      &:hover {
        background: darken($warn, 6%);
      }
    }
  }
  &--danger {
    background: #fef0f0;
    border-color: lighten($danger, 18%);
    .consumo-cta-icon {
      background: $danger;
    }
    .consumo-cta-btn {
      background: $danger;
      &:hover {
        background: darken($danger, 6%);
      }
    }
  }
}
.consumo-cta-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}
.consumo-cta-body {
  flex: 1;
  min-width: 0;
}
.consumo-cta-title {
  font-size: 14px;
  font-weight: 700;
  color: $primary;
  margin-bottom: 2px;
}
.consumo-cta-text {
  font-size: 12px;
  color: #606266;
  line-height: 1.45;
}
.consumo-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  border-radius: 10px;
  color: #fff !important;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none !important;
  transition: background 0.15s;
  flex-shrink: 0;
  white-space: nowrap;
  &:hover {
    text-decoration: none !important;
  }
}

/* Plan footer */
.consumo-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: $bg;
  border-radius: 12px;
  border: 1px dashed $border;
}
.consumo-footer-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.consumo-footer-label {
  font-size: 12px;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}
.consumo-footer-value {
  font-size: 14px;
  font-weight: 700;
  color: $primary;
}
.consumo-footer-link {
  font-size: 13px;
  font-weight: 600;
  color: $accent;
  text-decoration: none !important;
  &:hover {
    color: darken($accent, 8%);
  }
}

/* Mobile */
@media (max-width: 600px) {
  .consumo-reset {
    flex-direction: column;
    align-items: flex-start;
    padding: 14px 16px;
  }
  .consumo-bar-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .consumo-bar-count {
    align-self: flex-start;
  }
  .consumo-cta {
    flex-direction: column;
    align-items: flex-start;
  }
  .consumo-cta-btn {
    width: 100%;
    justify-content: center;
  }
  .consumo-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
